import { useCallback, useContext, useMemo } from 'react';

import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { TokenContext } from '@/Contexts/TokenContext';
import {
  ApplicationDataGridDetailsProps,
  ApplicationDataGridRowProps,
  ApplicationProps,
  NewApplicationType,
} from '@/models/Application';
import { ApplicationChangeLogEntry } from '@/models/ApplicationChangeLogEntry';
import { ApplicationExportModel } from '@/models/ApplicationExport';
import { DeleteApplicationResponse } from '@/models/DeleteApplicationResponse';
import { MassApproveResponse } from '@/models/MassApproveResponse';
import { ADFPaymentRequest } from '@/models/Payment';
import { PostApplicationResponse } from '@/models/PostApplicationResponse';
import { StandardProps } from '@/models/Standard';
import { ApplicationStatus, baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';
import { logError, logWarning } from '@/utils/errorHandlerUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { TreeLineReceiptDetails } from '@/models/TreeLineReceiptDetails';

export default function useApplication() {
  const { authResult } = useContext(TokenContext);
  const { locale } = useContext(LocaleContext);
  const { customFetch, customFetchResponse } = useCustomFetch();
  const { setAlert } = useContext(SnackbarContext);

  const applicationUrl = useMemo(() => `${baseUrl}applications`, []);

  const { updateHistoricApps } = useContext(DashboardContext);

  const applicationExport = useCallback(
    async (exportModel: ApplicationExportModel): Promise<unknown> => {
      const url = `${baseUrl}csv/AdvancedApplicationExport`;

      const response = await customFetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(exportModel),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getApplicationDataGridDetails = useCallback(
    async (applicationId: number): Promise<ApplicationDataGridDetailsProps> => {
      const response = await customFetch<ApplicationDataGridDetailsProps>(
        `${applicationUrl}/datagriddetails/${applicationId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        }
      );

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getTreeLineReceiptDetails = useCallback(
    async (applicationId: number, localeCode?: string): Promise<TreeLineReceiptDetails> => {
      const params = new URLSearchParams();
      if (localeCode != null && localeCode.trim() !== '') {
        params.set('localeCode', localeCode);
      }
      const query = params.toString();

      const response = await customFetch<TreeLineReceiptDetails>(
        `${applicationUrl}/treeLineReceipt/${applicationId}${query === '' ? '' : `?${query}`}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        }
      );

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getApplicationsPendingReview = useCallback(
    async (abortController: AbortController): Promise<ApplicationDataGridDetailsProps[]> => {
      const url = `${applicationUrl}/simple?pendingreviews=true`;
      const allApps = await customFetch<ApplicationDataGridDetailsProps[]>(url, {
        method: 'GET',
        signal: abortController.signal,
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return allApps;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getApplicationsPendingApprovalAnnouncement = useCallback(
    async (abortController: AbortController): Promise<ApplicationDataGridRowProps[]> => {
      const url = `${applicationUrl}/simple?pendingapprovalannouncement=true`;
      const allApps = await customFetch<ApplicationDataGridRowProps[]>(url, {
        method: 'GET',
        signal: abortController.signal,
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return allApps;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getHistoricApplications = useCallback(
    async (applicationCycleYear: string | null, abortController: AbortController) => {
      let url = `${applicationUrl}/simple`;

      const separator = url.indexOf('?') === -1 ? '?' : '&';

      // make sure the string can be converted to a number (2024, 2023, 2022, etc..)
      if (applicationCycleYear != null && !isNaN(Number(applicationCycleYear))) {
        url += `${separator}applicationCycleYear=${applicationCycleYear}`;
      }

      try {
        const response = await customFetch<ApplicationDataGridRowProps[]>(url, {
          method: 'GET',
          signal: abortController.signal,
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        updateHistoricApps(response);

        return response;
      } catch (error: unknown) {
        if (
          (error instanceof DOMException && error.name === 'AbortError') ||
          (typeof error === 'string' && error.includes('Data fetching aborted'))
        ) {
          logWarning(`Fetch aborted for historic applications`);
          return null;
        } else {
          logError(`Error fetching historic applications`, error);
          return null;
        }
      }
    },
    [applicationUrl, authResult.accessToken, customFetch, updateHistoricApps]
  );

  const getApplicationChangeLog = useCallback(
    async (applicationId: null | number): Promise<ApplicationChangeLogEntry[] | null> => {
      const url = `${applicationUrl}/changelog/${applicationId}`;

      const response = await customFetch<ApplicationChangeLogEntry[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });
      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getAppsForApplicantDashboard = useCallback(
    async (partnerId: number) => {
      const url = `${applicationUrl}/simple?partnerId=${partnerId}`;
      const allApps = await customFetch<ApplicationDataGridRowProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return allApps;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getSingleAppForApplicantDashboard = useCallback(
    async (appId: number): Promise<ApplicationDataGridRowProps | null> => {
      const url = `${applicationUrl}/simple?idlist=${appId}`;
      const response = await customFetch<ApplicationDataGridRowProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response.length > 0 ? response[0] : null;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getSummary = useCallback(
    async (applicationId: number | undefined): Promise<ArrayBuffer> => {
      if (applicationId == null) {
        logError(
          'Application ID is undefined when fetching summary.',
          new Error('Application ID is undefined')
        );

        setAlert({
          severity: 'error',
          message: 'Error fetching application summary - application ID is undefined.',
        });

        return new ArrayBuffer(0);
      }

      const url = `${baseUrl}applications/${applicationId}/print?summary=true`;
      const response = await customFetchResponse(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      try {
        return await response.arrayBuffer().then((data: ArrayBuffer) => {
          return data;
        });
      } catch (error) {
        logError(`Error fetching application summary for application.`, error);

        setAlert({
          severity: 'error',
          message: 'Error fetching application summary - please try again.',
        });

        return new ArrayBuffer(0);
      }
    },
    [authResult.accessToken, customFetchResponse, setAlert]
  );

  const getPrintApplication = useCallback(
    async (applicationId: number | null) => {
      if (applicationId == null) {
        logError(
          'Application ID is undefined when fetching print application.',
          new Error('Application ID is undefined')
        );

        setAlert({
          severity: 'error',
          message: 'Error fetching print application - application ID is undefined.',
        });
        return [];
      }

      const url = `${baseUrl}applications/${applicationId}/print`;

      const response = await customFetchResponse(url, {
        method: 'GET',
        headers: {
          //accept: "application/json",
          //"content-type": "application/json",
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      try {
        await response.arrayBuffer().then((data) => {
          const blob = new Blob([data], { type: 'application/pdf' });
          const objectUrl = URL.createObjectURL(blob);
          window.open(objectUrl);

          return data;
        });
      } catch (error) {
        logError(`Error fetching print application for application ${applicationId}.`, error);

        return [];
      }
    },
    [authResult.accessToken, customFetchResponse, setAlert]
  );

  const getOneApplication = useCallback(
    async (id: string | number): Promise<ApplicationProps | null> => {
      const response = await customFetch<ApplicationProps>(`${applicationUrl}/${id}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const getAllProgramStandards = useCallback(async (): Promise<StandardProps[]> => {
    const url = `${baseUrl}standards`;
    const response = await customFetch<StandardProps[]>(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    return response;
  }, [authResult, customFetch]);

  const postApplication = useCallback(
    async (postData: NewApplicationType): Promise<PostApplicationResponse> => {
      const response = await customFetch<PostApplicationResponse>(applicationUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(postData),
      });

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const updateApplication = useCallback(
    async (postData: ApplicationProps): Promise<ApplicationProps> => {
      const id = postData.id;
      const response = await customFetch<ApplicationProps>(applicationUrl + `/${id}`, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(postData),
      });

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const updateIsPendingApprovalAnnouncements = useCallback(
    async (applicationId: number, isPendingApprovalAnnouncement: boolean) => {
      const url = `${applicationUrl}/updateispendingapprovalannouncement/${applicationId}?isPendingApprovalAnnouncement=${isPendingApprovalAnnouncement}`;
      const response = await customFetch<ApplicationStatus>(url, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const postApplicationStatus = useCallback(
    async (applicationId: number, returnToApplicant: boolean): Promise<ApplicationStatus> => {
      const url = `${baseUrl}status`;
      const params = {
        applicationId: applicationId,
        returnToApplicant: returnToApplicant ? 1 : 0,
      };

      const response = await customFetch<ApplicationStatus>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(params),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const postSubmit = useCallback(
    async (appId: number, paymentRequest: ADFPaymentRequest | null): Promise<ApplicationStatus> => {
      let param = '';
      if (locale != null && locale != 'enUS') {
        param = `?language=${locale.slice(0, 2)}`;
      }
      const url = `${baseUrl}applications/${appId}/submit${param}`;

      const response = await customFetch<ApplicationStatus>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(paymentRequest === null ? { billingContact: {} } : paymentRequest),
      });

      return response;
    },
    [locale, customFetch, authResult]
  );

  const postResubmit = useCallback(
    async (appId: number): Promise<ApplicationStatus> => {
      let param = '';
      if (locale != null && locale != 'enUS') {
        param = `?language=${locale.slice(0, 2)}`;
      }
      const url = `${baseUrl}applications/${appId}/resubmit${param}`;

      const response = await customFetch<ApplicationStatus>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [locale, customFetch, authResult]
  );

  const massApproveApplications = useCallback(
    async (applicationIds: number[]): Promise<MassApproveResponse> => {
      const url = `${applicationUrl}/massapprove`;
      const response = await customFetch<MassApproveResponse>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(applicationIds),
      });

      return response;
    },
    [authResult, applicationUrl, customFetch]
  );

  const deleteApplication = useCallback(
    async (appId: number) => {
      const url = `${baseUrl}applications/${appId}`;
      const response = await customFetch<DeleteApplicationResponse>(url, {
        method: 'DELETE',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${authResult.accessToken}`,
          'Cache-Control': 'no-cache',
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  return {
    applicationExport,
    getPrintApplication,
    getTreeLineReceiptDetails,
    getApplicationDataGridDetails,
    getApplicationsPendingReview,
    getApplicationsPendingApprovalAnnouncement,
    getHistoricApplications,
    postApplication,
    getOneApplication,
    postApplicationStatus,
    postSubmit,
    postResubmit,
    getSummary,
    updateApplication,
    updateIsPendingApprovalAnnouncements,
    getAppsForApplicantDashboard,
    getSingleAppForApplicantDashboard,
    massApproveApplications,
    deleteApplication,
    getAllProgramStandards,
    getApplicationChangeLog,
  };
}
